import React from "react";
import { Tooltip } from "@mui/material";
import moment from "moment";
import LocationPin from "./LocationPin";
import { hm, timeOf, STATUS } from "./helpers";
import "./Attendance.scss";

/* A day drawn against the shift, so a short or late day is obvious without
   reading the numbers. */
const DayBar = ({ row, shift }) => {
    const dayStart = shift.startHour * 60;
    const daySpan = (shift.endHour - shift.startHour) * 60;

    /* hours only exist where a punch does, so there is nothing to draw */
    if (!row.checkIn) return <span className="att-bar att-bar--none" />;

    const inMin = moment(row.checkIn).hours() * 60 + moment(row.checkIn).minutes();
    const outMin = row.checkOut
        ? moment(row.checkOut).hours() * 60 + moment(row.checkOut).minutes()
        : inMin + row.grossMinutes;

    const left = Math.max(((inMin - dayStart) / daySpan) * 100, 0);
    const width = Math.min(((outMin - inMin) / daySpan) * 100, 100 - left);

    return (
        <span className="att-bar">
            <span className="att-bar-fill" style={{ left: `${left}%`, width: `${Math.max(width, 2)}%` }} />
        </span>
    );
};

/* The day-by-day record, newest first. Shared by both tabs so a day reads the
   same whether it is your own or someone on your team. */
const AttendanceLog = ({ rows = [], shift }) => {

    /* the range can run into the future for the calendar's sake; the log is a
       record of what happened, so those days are left out of it */
    const ordered = rows.filter((row) => row.status !== "upcoming").slice().reverse();

    return (
        <div className="table-responsive wh-table-shell">
            <table className="table">
                <thead>
                    <tr>
                        <th scope="col" className="wh-col-shrink">Date</th>
                        <th scope="col">Attendance</th>
                        <th scope="col" className="wh-col-shrink">Check in</th>
                        <th scope="col" className="wh-col-shrink">Check out</th>
                        <th scope="col" className="wh-col-shrink">Break</th>
                        <th scope="col" className="wh-col-shrink">Gross hours</th>
                        <th scope="col" className="wh-col-shrink">Status</th>
                    </tr>
                </thead>
                <tbody>
                    {ordered.map((row) => {
                        const meta = STATUS[row.status] || STATUS.absent;
                        const off = ["weekend", "holiday"].includes(row.status);

                        return (
                            <tr key={row.dateKey} className={off ? "att-off" : undefined}>
                                <td className="wh-col-shrink">
                                    <span className="att-date">
                                        <b>{moment(row.date).format("ddd, DD MMM")}</b>
                                        <em>{moment(row.date).format("YYYY")}</em>
                                    </span>
                                </td>
                                <td>
                                    {off
                                        ? <span className="att-off-note">{meta.label}</span>
                                        : <Tooltip placement="top" arrow
                                            title={row.checkIn
                                                ? `${timeOf(row.checkIn)} – ${timeOf(row.checkOut)}`
                                                : "No check-in recorded"}>
                                            <span className="att-bar-wrap">
                                                <DayBar row={row} shift={shift} />
                                            </span>
                                        </Tooltip>}
                                </td>
                                <td className="wh-col-shrink">
                                    <span className="att-cell-place">
                                        {timeOf(row.checkIn)}
                                        <LocationPin location={row.checkInLocation} label="Checked in" />
                                        {!row.checkIn && (
                                            <LocationPin location={row.leaveLocation} label="Requested from" />
                                        )}
                                    </span>
                                </td>
                                <td className="wh-col-shrink">
                                    <span className="att-cell-place">
                                        {timeOf(row.checkOut)}
                                        <LocationPin location={row.checkOutLocation} label="Checked out" />
                                    </span>
                                </td>
                                <td className="wh-col-shrink">{hm(row.breakMinutes)}</td>
                                <td className="wh-col-shrink">
                                    {row.grossMinutes ? hm(row.grossMinutes) : "--"}
                                </td>
                                <td className="wh-col-shrink">
                                    <span className={`wh-pill ${meta.tone}`}>{meta.label}</span>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

export default AttendanceLog;
